import useInfiniteScroll from 'react-infinite-scroll-hook'
import styled, { keyframes } from 'styled-components'

const gloss = keyframes`
  from {
    left: -150px;
  }
  to {
    left: 100%;
  }
`

const fade = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`

const StyledLoadingImageSkeleton = styled.div`
  background: var(--wg-gray-border);
  border-radius: 15px;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: -150px;
    top: 0;
    height: 100%;
    width: 150px;
    background: linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
    animation: ${gloss} 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  /* Mostly for storybook visual tests but valid class name to add if we want */

  &.reduce-motion {
    &::before {
      animation: none;
    }
  }

  @media (prefers-reduced-motion) {
    & {
      animation: ${fade} 1s infinite;

      &::before {
        animation: none;
      }
    }
  }
`

type ImageLoadingSkeletonProps = {
  loading?: boolean
  onVisible?: () => void
  hasNextPage?: boolean
  quantity?: number
  className?: string
}

/**
 * An image loading skeleton that can be used to load more as it will trigger onVisible when the component
 * becomes visible.
 *
 * @param {boolean} [loading = false]
 * @param {function} [onVisible]
 * @param {boolean} [hasNextPage = false]
 * @param {number} [quantity = 3]
 * @param {string} [className]
 * @constructor
 */
const ImageLoadingSkeleton = ({
  loading = false,
  onVisible = () => {},
  hasNextPage = false,
  quantity = 3,
  className
}: ImageLoadingSkeletonProps) => {
  const [infiniteScrollerRef] = useInfiniteScroll({
    loading,
    onLoadMore: onVisible,
    hasNextPage
  })

  // Minimum 1 (the one that'll trigger the load)
  if (quantity < 1) {
    quantity = 1
  }

  return hasNextPage || loading ? (
    <>
      {Array.from({ length: quantity - 1 })
        .fill('')
        .map((item, index) => (
          <StyledLoadingImageSkeleton key={index} className={className} />
        ))}
      <StyledLoadingImageSkeleton className={className} ref={infiniteScrollerRef} />
    </>
  ) : null
}

ImageLoadingSkeleton.displayName = 'ImageLoadingSkeleton'

export default ImageLoadingSkeleton
